import React, { FunctionComponent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { map, find } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { useSwipeable } from 'react-swipeable';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import RegularButton from '../../../../components/RegularButton';
import ReferenceCard from '../ReferenceCard';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { resendSurveyRoutine } from '../../routines';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IResendSurvey } from '../../../../common/models/reference/IResendSurvey';
import ModalWindow from '../../../../components/ModalWindow';
import EmailTextArea from '../../../../components/EmailTextArea';
import { ReactComponent as Plus } from '../../../../assets/icons/plus.svg';
import styles from './styles.module.scss';
import Checkbox from '../../../../components/Checkbox';

interface IRefProgressPagePage extends RouteComponentProps {
  references: IReferenceCandidateForm[];
  messageTemplate: string;
  onAddMoreReferences: IBindingAction;
  resendSurvey: IBindingCallback1<IResendSurvey>;
}

const RefProgressPage: FunctionComponent<IRefProgressPagePage> = ({
  references,
  messageTemplate,
  onAddMoreReferences,
  resendSurvey
}) => {
  const [showReminder, setShowReminder] = useState(false);
  const [currentRefId, setCurrentRefId] = useState('');
  const [message, setMessage] = useState(messageTemplate);
  const [sendSMS, setSendSMS] = useState(false);
  const currentRef = find(r => r.id === currentRefId, references);

  useEffect(() => {
    if (showReminder) {
      setSendSMS(Boolean(currentRef?.phoneNumber));
    }
  }, [showReminder]);

  const swipeHandler = useSwipeable({
    onSwipedLeft: onAddMoreReferences,
    trackMouse: false
  });

  const openModal = (id: string) => {
    setShowReminder(true);
    setCurrentRefId(id);
  };

  const send = () => {
    resendSurvey({ id: currentRefId, message, sendSMS });
    setShowReminder(false);
  };

  return references && (
    <div {...swipeHandler} className="cf-container cf-container-with-bttns">
      <div className="cf-title mb-2">
        <FormattedMessage id="candidateForm.monitor.title" defaultMessage="Monitor reference progress" />
      </div>
      <p className="cf-subtitle mb-4">
        <FormattedMessage
          id="candidateForm.momitor.subtitle"
          defaultMessage="Monitor your references’ progress and send reminders."
        />
      </p>
      <div className={styles.cards}>
        {references && map(ref => (
          <ReferenceCard
            key={ref.id}
            showProgress
            reference={ref}
            onResend={() => openModal(ref.id || '')}
          />
        ),
        references)}
      </div>
      <ModalWindow
        isShown={showReminder}
        onHide={() => setShowReminder(false)}
        contentClassName="medium-modal modal-wrapper"
      >
        <div className="d-flex flex-column">
          <div className="custom-modal-header">
            <FormattedMessage id="candidateForm.sendReminder" defaultMessage="Send Reminder" />
          </div>
          <div className="p-3">
            <div className={styles.checkboxContainer}>
              <Checkbox
                checked={sendSMS}
                onChange={({ currentTarget }) => setSendSMS(currentTarget.checked)}
                label={{ id: 'candidateForm.email.sendSMS', defaultMessage: 'Send as SMS text message too?' }}
              />
            </div>
            <div className={styles.emailWrp}>
              <EmailTextArea
                customEmailHeader={`${currentRef?.firstName},`}
                message={message}
                onChange={setMessage}
              />
            </div>
          </div>
          <div className="modal-footer-bttns">
            <RegularButton
              onClick={send}
              textIntlId="candidateForm.resend"
              defaultMessage="Resend"
            />
          </div>
        </div>
      </ModalWindow>
      <div className="cf-buttons">
        <RegularButton
          size="lg"
          variant="secondary"
          textIntlId="candidateForm.addReference"
          defaultMessage="Add reference"
          icon={<Plus />}
          iconPosition="right"
          onClick={onAddMoreReferences}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ referenceRequest: { references, messageTemplate }
}: IAppState) => ({ references, messageTemplate });

const mapDispachToProps = {
  resendSurvey: resendSurveyRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(withRouter(RefProgressPage));
